import { ONBOARDING_THREAD_ID } from '@/utils/constants';

export const onboardingMessagesEN = {
  'step-0': {
    messages: {
      role: 'ai',
      content: [],
    },
  },
  'step-1': {
    messages: {
      role: 'ai',
      content: [
        {
          id: 'onb-0',
          timestamp: '2024-11-21T06:35:39.224335+00:00',
          role: 'human',
          threadId: ONBOARDING_THREAD_ID,
          content:
            'Which drivers need support due to an overall rating below 70 on average in the last month and list their ratings?',
        },
        {
          id: 'onb-1',
          timestamp: '2024-11-21T06:36:09.224354+00:00',
          role: 'ai',
          threadId: ONBOARDING_THREAD_ID,
          data_visualization: {
            default_data_visualization: 'table',
            x_axis: 'Driver Name',
          },
          content:
            'The following analysis covers drivers with an average overall rating below 70 for the period from June 1st, 2026 to June 30th, 2026. A total of 12 drivers fall below the 70-point threshold and are listed below, ordered from lowest to highest rating.\n\n' +
            '```json\n\n' +
            '{"table": [{"Driver Name": "Lucas Auernhammer", "Total Rating": 23.0}, {"Driver Name": "Christian Scheunemann", "Total Rating": 25.0}, {"Driver Name": "Bruno Ranft", "Total Rating": 26.0}, {"Driver Name": "Ute Meyjohann", "Total Rating": 26.0}, {"Driver Name": "Erhard Brauer", "Total Rating": 28.0}, {"Driver Name": "Margit Br\\u00fcck", "Total Rating": 28.0}, {"Driver Name": "Torsten Mustermann", "Total Rating": 39.0}, {"Driver Name": "Marius Mehl", "Total Rating": 42.0}, {"Driver Name": "Thomas Mustermann", "Total Rating": 43.0}, {"Driver Name": "Bernd M\\u00fcller", "Total Rating": 44.0}, {"Driver Name": "Maik Fleischhauer", "Total Rating": 44.0}, {"Driver Name": "Sabine Lindenmann", "Total Rating": 48.0}]}\n```' +
            '\n\nSince you asked to identify drivers who need support, here is a breakdown by performance tier:\n\n' +
            '**Critical — Ratings below 30 (immediate attention recommended):**\n\n' +
            '- **Lucas Auernhammer** (23.0), **Christian Scheunemann** (25.0), **Bruno Ranft** (26.0), **Ute Meyjohann** (26.0), **Erhard Brauer** (28.0), and **Margit Brück** (28.0) are the lowest-rated drivers in the fleet this month. These drivers should be prioritized for individual coaching and targeted support as soon as possible.\n\n' +
            '**Moderate concern — Ratings between 30 and 50:**\n\n' +
            '- **Torsten Mustermann** (39.0), **Marius Mehl** (42.0), **Thomas Mustermann** (43.0), **Bernd Müller** (44.0), **Maik Fleischhauer** (44.0), and **Sabine Lindenmann** (48.0) are also below the 70-point threshold and would benefit from structured coaching to improve their overall performance.\n\n' +
            'To understand the root causes of these low ratings, it is recommended to review the individual sub-ratings (coasting, braking, cruise control, overspeed, and acceleration) for each driver. MAN ProfiDrive and the Connected CoDriver program offer targeted training and efficiency coaching that can help these drivers improve their scores.',
        },
      ],
    },
  },

  'step-2': {
    messages: {
      role: 'ai',
      content: [
        {
          id: 'onb-2',
          timestamp: '2024-11-21T06:36:39.224360+00:00',
          role: 'human',
          threadId: ONBOARDING_THREAD_ID,
          content: 'What is the reason for the low rating of Bruno Ranft?',
        },
        {
          id: 'onb-3',
          timestamp: '2024-11-21T06:37:09.224366+00:00',
          role: 'ai',
          threadId: ONBOARDING_THREAD_ID,
          data_visualization: {
            default_data_visualization: 'table',
          },
          content:
            '**Here are the detailed driver ratings for Bruno Ranft for June 1st, 2026 to June 30th, 2026.**\n\n' +
            '```json\n\n' +
            '{"table": [{"KPI": "Driver Name", "": "Bruno Ranft"}, {"KPI": "Total Rating", "": 26.0}, {"KPI": "Coasting Rating", "": 20.0}, {"KPI": "Braking Behavior Rating", "": 20.0}, {"KPI": "Acceleration Pedal Rating", "": 40.0}, {"KPI": "Cruise Control Usage Rating", "": 50.0}, {"KPI": "Overspeed Rating", "": 0.0}]}\n```' +
            "\n\nBruno Ranft's total rating of 26.0 is among the lowest in the fleet this month, and the sub-ratings reveal the key drivers behind this result:\n\n" +
            '**Overspeed (0.0) — Most critical area:**\n\n' +
            'A score of 0.0 indicates that Bruno Ranft is spending a significant portion of driving time above the permitted speed limits. This is the single most impactful factor pulling down the overall rating and should be addressed as a top priority.\n\n' +
            '**Coasting (20.0) and Braking Behavior (20.0) — Significant weaknesses:**\n\n' +
            'Both scores are very low, suggesting that Bruno Ranft is not making effective use of coasting opportunities and is relying heavily on active braking rather than anticipatory driving.\n\n' +
            '**Acceleration Pedal (40.0) — Below average:**\n\n' +
            'The acceleration pedal rating indicates that throttle inputs are not optimally smooth or efficient.\n\n' +
            '**Cruise Control Usage (50.0) — Moderate:**\n\n' +
            'While this is the highest sub-rating for Bruno Ranft, a score of 50.0 still indicates that cruise control is underutilized on suitable road sections.\n\n' +
            '**Summary of recommended focus areas (in priority order):**\n\n' +
            '1. **Overspeed** — Strict adherence to speed limits is the most urgent improvement needed.\n\n' +
            '2. **Coasting & Braking** — Anticipatory driving techniques to reduce unnecessary braking.\n\n' +
            '3. **Acceleration** — Smoother, more gradual throttle inputs.\n\n' +
            '4. **Cruise Control** — Greater use on appropriate road sections.\n\n' +
            "MAN's **ProfiDrive** training program and the **Connected CoDriver** coaching service are well-suited to address all of these areas through targeted, practical driver coaching.",
        },
      ],
    },
  },

  'step-3': {
    messages: {
      role: 'ai',
      content: [
        {
          id: 'onb-4',
          timestamp: '2024-11-21T06:37:39.224372+00:00',
          role: 'human',
          threadId: ONBOARDING_THREAD_ID,
          content: 'How is the brake rating put together?',
        },
        {
          id: 'onb-5',
          timestamp: '2024-11-21T06:38:09.224378+00:00',
          role: 'ai',
          threadId: ONBOARDING_THREAD_ID,
          content:
            'The brake rating in MAN Perform is composed of two key factors:\n\n' +
            '**1. Service Brake Distance Ratio**\n\n' +
            '- The total distance travelled using the service brake is measured as a percentage of overall driving distance.\n\n' +
            '- A score of **100%** requires less than **1%** of the total distance to be braked using the service brake.\n\n' +
            '- A score of **0%** is given when more than **5.5%** of the distance is braked using the service brake.\n\n' +
            '**2. Wear-Free Brake vs. Service Brake Ratio**\n\n' +
            '- The ratio between wear-free braking (retarder and engine brake) and service brake usage is evaluated.\n\n' +
            '- A score of **100%** requires a ratio of **5:1 or more** (wear-free brake to service brake).\n\n' +
            '- A score of **0%** is given at a ratio of **0.5:1 or lower**.\n\n' +
            '**Recommendation for Improvement**\n\n' +
            'Whenever traffic conditions allow, drivers should use the **retarder or engine brake** instead of the service brake. Anticipatory driving — releasing the accelerator early and coasting before deceleration phases — naturally reduces reliance on the service brake and improves this rating.' +
            '\n\n1. [BA eTGS MFL 2026 Electric - Page 87 ](https://fleetassistant-staging.rio.cloud/docs/language=EN/market=all/BA_eTGS_MFL_2026_Electric.pdf#page=87)\n\n' +
            '2. [BA eTGX MFL 2026 Electric - Page 79 ](https://fleetassistant-staging.rio.cloud/docs/language=EN/market=all/BA_eTGX_MFL_2026_Electric.pdf#page=79)\n\n' +
            '3. [Perform Driver Review 1 - Page 3 ](https://fleetassistant-staging.rio.cloud/docs/language=EN/market=all/Perform_Driver_Review_1.pdf#page=3)\n\n' +
            '4. [Perform Webinar 1 - Page 5 ](https://fleetassistant-staging.rio.cloud/docs/language=EN/market=all/Perform_Webinar_1.pdf#page=5)\n\n' +
            '5. [2026 Perform Ratings - Page 5 ](https://fleetassistant-staging.rio.cloud/docs/language=EN/market=all/2026_Perform_Ratings.pdf#page=5)',
        },
      ],
    },
  },

  'step-4': {
    messages: {
      role: 'ai',
      content: [
        {
          id: 'onb-6',
          timestamp: '2024-11-21T06:38:39.224384+00:00',
          role: 'human',
          threadId: ONBOARDING_THREAD_ID,
          content: 'What can my driver Bruno Ranft who has a low brake pedal rating do to improve?',
        },
        {
          id: 'onb-7',
          timestamp: '2024-11-21T06:39:09.224403+00:00',
          role: 'ai',
          threadId: ONBOARDING_THREAD_ID,
          data_visualization: {
            default_data_visualization: 'table',
          },
          content:
            "Bruno Ranft's Braking Behavior Rating for the period from June 2nd, 2026 to July 2nd, 2026 stands at **20 out of 100**, which is significantly below the optimal level and indicates a clear area for improvement.\n\n" +
            '- **KPI** Driver Name: Bruno Ranft\n' +
            '- **KPI** Braking Behavior Rating: 20.0\n\n' +
            '## What the Rating Measures\n\n' +
            '- **Service brake distance ratio**: How much of the total driving distance is braked using the service brake.\n' +
            '- **Wear-free brake vs. service brake ratio**: The ratio of retarder/engine brake usage to service brake usage.\n\n' +
            'A score of 20 suggests Bruno Ranft is relying heavily on the service brake and making insufficient use of wear-free braking systems.\n\n' +
            '## Concrete Steps Bruno Ranft Can Take to Improve\n\n' +
            '1. **Prioritize the retarder and engine brake**.\n\n' +
            '2. **Practice anticipatory driving** by releasing the accelerator earlier.\n\n' +
            '3. **Reduce service brake distance share** to below 1% of total driving distance.\n\n' +
            '4. **Combine coasting with wear-free braking**.\n\n' +
            '5. **Consider MAN ProfiDrive training** for targeted coaching.\n\n' +
            'By consistently applying wear-free braking and anticipatory driving, Bruno Ranft can realistically move his braking rating from 20 toward the 70–100 range, reducing brake wear costs and improving overall fleet efficiency.' +
            '\n\n\n\nBased on the following documents the answer was generated (ranked by relevance):\n\n' +
            '1. [Perform Webinar 1 - Page 5 ](https://fleetassistant-staging.rio.cloud/docs/language=EN/market=all/Perform_Webinar_1.pdf#page=5)\n\n' +
            '2. [Perform Webinar 1 - Page 6 ](https://fleetassistant-staging.rio.cloud/docs/language=EN/market=all/Perform_Webinar_1.pdf#page=6)\n\n' +
            '3. [Perform Driver Review 1 - Page 3 ](https://fleetassistant-staging.rio.cloud/docs/language=EN/market=all/Perform_Driver_Review_1.pdf#page=3)',
        },
      ],
    },
  },
};
